/**
 * @file update-selling-price.tsx
 * @module app/modals/update-selling-price
 * @description Renders the update selling price modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  Form,
  Button,
  Modal,
  Card,
  Row,
  Col,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import CrossSvg from "../../_admin/assets/media/close.png";
import clsx from "clsx";
import Cartoon from "../../_admin/assets/media/product/cartoon.png";
import Dimension from "../../_admin/assets/media/product/dimension.webp";
import AddMore from "../../_admin/assets/media/product/addMore.png";
import { Link } from "react-router-dom";
import Remove from "../../_admin/assets/media/product/remove.png";
import BlurCartoon from "../../_admin/assets/media/product/blurCartoon.webp";
import { useEffect, useState } from "react";
import { error, success } from "../../Global/toast";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  Piece,
} from "../../utils/constants";
import APICallService from "../../api/apiCallService";
import { inventory, PURCHASEORDER } from "../../api/apiEndPoints";

interface IValidation {
  sellingAmount: boolean;
  sellingDiscountAmt: boolean;
  sellingDiscountsByQuantities: {
    min: boolean;
    max: boolean;
    discountAmt: boolean;
  }[];
}

const UpdateSellingPrice = (props: any) => {
  const [loading, setLoading] = useState(false);
  const [variantData, setVariantData] = useState<any>();
  const [generalData, setGeneralData] = useState<any>([]);
  const [validation, setValidation] = useState<IValidation[]>([]);
  const [oldData, setOldData] = useState<any>();

  useEffect(() => {
    const tempData: any = { ...props.variantData };
    const tempValidationMain = [...validation];
    tempData.quantityTypes = tempData?.quantityTypes?.map((item: any) => {
      let temp: any = { ...item };
      let tempValidation: any = {
        sellingAmount: false,
        sellingDiscountAmt: false,
        sellingDiscountsByQuantities: [],
      };
      if (temp.sellingAmount == 0) {
        temp.sellingAmount = temp.amount;
        temp.sellingDiscountAmt = temp.discountAmt;
        if (temp.discountByQuantitiesEnabled) {
          temp.sellingDiscountByQuantitiesEnabled = true;
          temp.sellingDiscountsByQuantities = item?.discountsByQuantities;
        }
      }
      temp.sellingDiscountsByQuantities =
        temp?.sellingDiscountsByQuantities?.map((qItem: any) => {
          tempValidation.sellingDiscountsByQuantities.push({
            min: false,
            max: false,
            discountAmt: false,
          });
          return {
            ...qItem,
            discountAmt: qItem.discountAmt || 0,
          };
        });
      tempValidationMain.push(tempValidation);
      return temp;
    });
    setVariantData(tempData);
    setValidation(tempValidationMain);
    setOldData(JSON.parse(JSON.stringify(tempData)));
  }, []);

  const checkValidation = async (
    generalTemp: any,
    name: string,
    combinationIndex: number,
    productIndex: number
  ) => {
    //  let tempValidation = [...generalDataValidation];
    let tempValidation: any = [];
    switch (name) {
      case "cartoonDimenisionL":
      case "cartoonDimenisionW":
      case "cartoonWeight":
      case "cartoonPrice":
      case "cartoonDimenisionH":
      case "dozenDimenisionH":
      case "dozenDimenisionL":
      case "dozenDimenisionW":
      case "dozenPrice":
      case "dozenWeight":
      case "pieceDimenisionH":
      case "pieceDimenisionL":
      case "pieceDimenisionW":
      case "piecePrice":
      case "cartoonDozens":
      case "cartoonPieces":
      case "piecesDozens":
      case "pieceWeight":
        tempValidation[productIndex].combinations[combinationIndex].dimensions[
          name
        ] = false;
        if (
          generalTemp[productIndex].combinations[combinationIndex].dimensions[
            name
          ] === ""
        ) {
          tempValidation[productIndex].combinations[
            combinationIndex
          ].dimensions[name] = true;
        }
        break;
      case "cartoonDiscountPrice":
        if (
          generalTemp[productIndex].combinations[combinationIndex].dimensions[
            "cartoonDiscount"
          ] === "0"
        ) {
          if (
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "cartoonPrice"
            ] &&
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "cartoonPrice"
            ] !== ""
          ) {
            if (
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["cartoonDiscountPrice"]
              ) >
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["cartoonPrice"]
              )
            ) {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = true;
            } else {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = false;
            }
          }
        }
        break;
      case "dozenDiscountPrice":
        if (
          generalTemp[productIndex].combinations[combinationIndex].dimensions[
            "dozenDiscount"
          ] === "0"
        ) {
          if (
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "dozenPrice"
            ] &&
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "dozenPrice"
            ] !== ""
          ) {
            if (
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["dozenDiscountPrice"]
              ) >
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["dozenPrice"]
              )
            ) {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = true;
            } else {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = false;
            }
          }
        }
        break;
      case "pieceDiscountPrice":
        if (
          generalTemp[productIndex].combinations[combinationIndex].dimensions[
            "pieceDiscount"
          ] === "0"
        ) {
          if (
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "piecePrice"
            ] &&
            generalTemp[productIndex].combinations[combinationIndex].dimensions[
              "piecePrice"
            ] !== ""
          ) {
            if (
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["pieceDiscountPrice"]
              ) >
              parseInt(
                generalTemp[productIndex].combinations[combinationIndex]
                  .dimensions["piecePrice"]
              )
            ) {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = true;
            } else {
              tempValidation[productIndex].combinations[
                combinationIndex
              ].dimensions[name] = false;
            }
          }
        }
        break;
      default:
      // code block
    }
    //  setGeneralDataValidation(tempValidation);
  };
  const handleOnKeyPress = (event: any) => {
    var charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return false;
    }
    return true;
  };
  const handleQuantityChangeProduct = (
    value: any,
    combinationIndex: number,
    name: string,
    index: number,
    param: string,
    productIndex: number
  ) => {
    let temp = [...generalData];
    temp[productIndex].combinations[combinationIndex].dimensions[name][index][
      param
    ] = value;
    setGeneralData(temp);
  };
  const popover = (
    <Popover id="popover-basic  ">
      <Popover.Body className="p-2 bg-black border-r10px">
        <img src={Dimension} height={214} width={250} alt="" />
      </Popover.Body>
    </Popover>
  );

  const handleToggle = (index: any, value: boolean) => {
    const temp = { ...variantData };
    const tempValidation = [...validation];
    temp.quantityTypes[index].sellingDiscountByQuantitiesEnabled = value;
    if (value) {
      temp.quantityTypes[index].sellingDiscountsByQuantities = [
        {
          min: "",
          max: "",
          discountAmt: "",
        },
      ];
      tempValidation[index].sellingDiscountsByQuantities = [
        {
          min: false,
          max: false,
          discountAmt: false,
        },
      ];
    } else {
      temp.quantityTypes[index].sellingDiscountsByQuantities = [];
      tempValidation[index].sellingDiscountsByQuantities = [];
    }
    setVariantData(temp);
    setValidation(tempValidation);
  };

  const handleQuantityChagne = (
    value: any,
    name: any,
    varIndex: any,
    qIndex: any
  ) => {
    const temp = { ...variantData };
    const tempValidation: any = [...validation];
    temp.quantityTypes[varIndex].sellingDiscountsByQuantities[qIndex][name] =
      value;
    tempValidation[varIndex].sellingDiscountsByQuantities[qIndex][name] =
      value == 0 || (value + "").length == 0;
    setVariantData(temp);
    setValidation(tempValidation);
  };

  const handleAddMoreProduct = (index: any) => {
    const temp = { ...variantData };
    const tempValidation = [...validation];
    temp.quantityTypes[index].sellingDiscountsByQuantities.push({
      min: "",
      max: "",
      discountAmt: "",
    });
    tempValidation[index].sellingDiscountsByQuantities.push({
      min: false,
      max: false,
      discountAmt: false,
    });
    setVariantData(temp);
    setValidation(tempValidation);
  };

  const handleRemoveProduct = (index: any, qIndex: any) => {
    const temp = { ...variantData };
    const tempValidation = [...validation];
    temp.quantityTypes[index].sellingDiscountsByQuantities.splice(qIndex, 1);
    tempValidation[index].sellingDiscountsByQuantities.splice(qIndex, 1);
    setVariantData(temp);
    setValidation(tempValidation);
  };

  const handleChange = (value: any, index: any, name: any) => {
    const temp = { ...variantData };
    const tempValidation: any = [...validation];
    temp.quantityTypes[index][name] = value;
    if (name != "sellingDiscountAmt") {
      tempValidation[index][name] = value == 0 || (value + "").length == 0;
    }
    if (name == "sellingDiscountAmt") {
      tempValidation[index].sellingDiscountAmt = false;
    }
    setVariantData(temp);
    setValidation(tempValidation);
  };

  function checkIsEmpty(value: any) {
    return value == 0 || !value || (value + "").length == 0;
  }

  const handleSave = async () => {
    const tempValidation = [...validation];
    variantData.quantityTypes?.forEach((item: any, index: number) => {
      tempValidation[index].sellingAmount = checkIsEmpty(item?.sellingAmount);
      if (item.sellingDiscountByQuantitiesEnabled) {
        tempValidation[index].sellingDiscountAmt = false;
        item.sellingDiscountsByQuantities?.forEach(
          (qItem: any, qIndex: number) => {
            tempValidation[index].sellingDiscountsByQuantities[qIndex].min =
              checkIsEmpty(qItem.min);
            tempValidation[index].sellingDiscountsByQuantities[qIndex].max =
              checkIsEmpty(qItem.max);
            tempValidation[index].sellingDiscountsByQuantities[
              qIndex
            ].discountAmt = qItem.discountAmt && parseInt(qItem.discountAmt) > item?.sellingAmount;
          }
        );
      } else {
        tempValidation[index].sellingDiscountAmt =
          item?.sellingDiscountAmt &&
          parseInt(item?.sellingDiscountAmt) > item?.sellingAmount;
      }
    });

    setValidation(tempValidation);
    const isValid = tempValidation.some((item) => {
      return (
        item.sellingAmount ||
        item.sellingDiscountAmt ||
        item.sellingDiscountsByQuantities?.some(
          (qItem) => qItem.min || qItem.max || qItem.discountAmt
        )
      );
    });

    if (isValid) {
      return;
    }

    setLoading(true);

    let message = getLogsMessage();
    const data: any = {
      variants: variantData?.quantityTypes?.map((item: any) => ({
        variant: variantData?.variant?._id,
        quantityTypes: variantData?.quantityTypes.map(
          (qItem: any, index: number) => {
            delete qItem.dimension;
            return {
              ...qItem,
              sellingDiscountAmt: qItem?.sellingDiscountAmt || 0,
              sellingDiscountAmtEnabled:
                qItem.sellingDiscountByQuantitiesEnabled
                  ? false
                  : qItem?.sellingDiscountAmt || 0
                  ? true
                  : false,
              sellingAmount: qItem?.sellingAmount
                ? qItem?.sellingAmount
                : qItem?.amount,
            };
          }
        ),
      })),
      recordId: props?.details?._id,
      refKey: props?.deliveryRefKey,
      // shipment: props?.details?.shipment,
      // type: 1,
      // shipmentFor: 1,
      // businessId: props?.business?.reference || "",
      // warehouseId: props?.warehouse?.reference || "",
    };
    if (message && message.length) {
      data.auditLogMessage = message.join(", ");
    }
    const apiCallService = new APICallService(
      PURCHASEORDER.updateSellingPrice,
      data,
      { id: props?.details?._id },
      "",
      "",
      false
    );
    const response = await apiCallService.callAPI();
    if (response) {
      success("Selling price updated successfully!");
      const qt = variantData?.quantityTypes?.[0];
      const updatedPayload = {
        variantId: variantData?.variant?._id,
        // assume single quantity being edited (caller opens modal with single qty in array)
        quantityType: qt?.type,
        sellingAmount: qt?.sellingAmount,
        sellingDiscountAmt: qt?.sellingDiscountAmt,
        sellingDiscountAmtEnabled: qt?.sellingDiscountAmtEnabled,
        sellingDiscountByQuantitiesEnabled: qt?.sellingDiscountByQuantitiesEnabled,
        sellingDiscountsByQuantities: qt?.sellingDiscountsByQuantities,
        deliveryRefKey: props?.deliveryRefKey || null,
      };

      props.onHide();
      // pass updated payload back to parent so it can update UI without refetch
      if (props.onSave) {
        await props.onSave(updatedPayload);
      }
    }
    setLoading(false);
  };

  const getTitle = (type: any) => {
    if (type == Dozen) return "Dozen";
    if (type == CartonWithDozens) return "C(D)";
    if (type == CartonWithPieces) return "C(P)";
    if (type == Piece) return "Pieces";
  };
  const renderCartoonDetails = (dimension: any, productIndex: number) => {
    let cartoonDetails = (
      <>
        <Row className="p-6">
          <h2 className="fs-20 fw-bolder text-primary mb-6">
            Add {getTitle(dimension.type)} details
          </h2>
          <Col lg={12}>
            <Row className="align-items-center">
              <Col md={3}>
                <div className="symbol symbol-lg-200px symbol-175px symbol-fixed bg-light position-relative">
                  <img className="img-fluid" src={Cartoon} alt="" />
                  <div className="text-center mt-3">
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={popover}
                    >
                      <Link to="#" className="fs-14 fw-700">
                        How to measure?
                      </Link>
                    </OverlayTrigger>
                  </div>
                </div>
              </Col>
              <Col md={9}>
                <Row className="align-items-center mt-md-0 mt-5">
                  <Col lg={12}>
                    <Row>
                      <Col md={6}>
                        <Row className="align-items-center">
                          <Col lg={4} className="mb-3">
                            <label
                              htmlFor=""
                              className="fs-16 fw-500 mb-lg-0 mb-3 required"
                            >
                              Selling price of a {getTitle(dimension?.type)}
                            </label>
                          </Col>
                          <Col lg={8} className="mb-3">
                            <div className="input-group input-group-solid h-60px border">
                              <span
                                className={clsx(
                                  "input-group-text fs-16 fw-600 text-dark bg-white ",
                                  validation[productIndex].sellingAmount
                                    ? "border-danger border-right-0"
                                    : "border-0 "
                                )}
                                id="basic-addon1"
                              >
                                TSh
                              </span>
                              <input
                                type="text"
                                className={clsx(
                                  "form-control form-control-lg bg-white text-black fs-16 fw-600",
                                  validation[productIndex].sellingAmount
                                    ? "border-danger border-left-0"
                                    : "border-0 "
                                )}
                                placeholder="Original price"
                                value={dimension.sellingAmount}
                                onChange={(event: any) => {
                                  handleChange(
                                    event.target.value,
                                    productIndex,
                                    "sellingAmount"
                                  );
                                }}
                                onKeyPress={(event: any) => {
                                  handleOnKeyPress(event);
                                }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      {!dimension.sellingDiscountByQuantitiesEnabled ? (
                        <Col md={6}>
                          <Row className="align-items-center">
                            <Col lg={4} className="mb-3">
                              <label htmlFor="" className="fs-16 fw-500">
                                Discounted price of a{" "}
                                {getTitle(dimension?.type)}
                              </label>
                            </Col>
                            <Col lg={8} className="mb-3">
                              <div className="input-group input-group-solid h-60px border">
                                <span
                                  className={clsx(
                                    "input-group-text fs-16 fw-600 text-dark bg-white ",
                                    validation[productIndex].sellingDiscountAmt
                                      ? "border-danger border-right-0"
                                      : "border-0 "
                                  )}
                                  id="basic-addon1"
                                >
                                  TSh
                                </span>
                                <input
                                  type="text"
                                  className={clsx(
                                    "form-control form-control-lg bg-white text-black fs-16 fw-600",
                                    validation[productIndex].sellingDiscountAmt
                                      ? "border-danger border-left-0"
                                      : "border-0 "
                                  )}
                                  placeholder="Discount price"
                                  value={dimension.sellingDiscountAmt}
                                  onChange={(event: any) => {
                                    handleChange(
                                      event.target.value,
                                      productIndex,
                                      "sellingDiscountAmt"
                                    );
                                  }}
                                  onKeyPress={(event: any) => {
                                    handleOnKeyPress(event);
                                  }}
                                />
                              </div>
                            </Col>
                            {false ? (
                              <>
                                <Col lg={4} className="mb-3"></Col>
                                <Col lg={8} className="mb-3">
                                  <label
                                    htmlFor=""
                                    className="fs-12 fw-500 text-danger"
                                  >
                                    Discount Price must be less than or equal to
                                    original price
                                  </label>
                                </Col>
                              </>
                            ) : (
                              <></>
                            )}
                          </Row>
                        </Col>
                      ) : (
                        <></>
                      )}
                    </Row>
                  </Col>
                  <Col xs={12} className=" text-start">
                    <label htmlFor="" className="fs-16 fw-500">
                      Do you offer discount by total number of quantities?
                    </label>
                  </Col>
                  <Col xs={12} className="mt-3">
                    <Row className="align-items-center">
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="0"
                            id="flexRadioChecked"
                            checked={
                              !dimension.sellingDiscountByQuantitiesEnabled
                            }
                            onChange={(event: any) => {
                              handleToggle(productIndex, false);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            No
                          </label>
                        </div>
                      </Col>
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="1"
                            id="flexRadioChecked"
                            checked={
                              dimension.sellingDiscountByQuantitiesEnabled
                            }
                            onChange={(event: any) => {
                              handleToggle(productIndex, true);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            Yes
                          </label>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        {dimension.sellingDiscountByQuantitiesEnabled ? (
          <>
            <div className="separator my-2"></div>
            <Row className="p-6 pb-8">
              <Col lg={12}>
                <div>
                  <h2 className="fs-22 fw-bolder text-dark">
                    Discount by total number of quantities
                  </h2>
                </div>
                {dimension.sellingDiscountsByQuantities.map(
                  (val: any, index: number) => {
                    return (
                      <>
                        <Row className="align-items-center mt-6">
                          <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                            <Row className="align-items-center">
                              <Col md={5}>
                                <label className="fs-16 fw-500 mb-md-0 mb-3">
                                  Minimum qty.
                                </label>
                              </Col>
                              <Col md={7}>
                                <div
                                  className={`input-group input-group-solid h-60px border ${
                                    validation[productIndex]
                                      ?.sellingDiscountsByQuantities[index]?.min
                                      ? "border-danger"
                                      : ""
                                  }`}
                                >
                                  <input
                                    type="text"
                                    className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                    placeholder="Original price"
                                    value={val.min}
                                    onChange={(event: any) => {
                                      handleQuantityChagne(
                                        event.target.value,
                                        "min",
                                        productIndex,
                                        index
                                      );
                                    }}
                                    onKeyPress={(event: any) => {
                                      handleOnKeyPress(event);
                                    }}
                                  />
                                  <span
                                    className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                    id="basic-addon1"
                                  >
                                    {" "}
                                    {getTitle(dimension?.type)}
                                  </span>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                            <Row className="align-items-center">
                              <Col md={5}>
                                <label className="fs-16 fw-500 mb-md-0 mb-3">
                                  Maximum qty.
                                </label>
                              </Col>
                              <Col md={7}>
                                <div
                                  className={`input-group input-group-solid h-60px border ${
                                    validation[productIndex]
                                      ?.sellingDiscountsByQuantities[index]?.max
                                      ? "border-danger"
                                      : ""
                                  }`}
                                >
                                  <input
                                    type="text"
                                    className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                    placeholder="Original price"
                                    value={val.max}
                                    onChange={(event: any) => {
                                      handleQuantityChagne(
                                        event.target.value,
                                        "max",
                                        productIndex,
                                        index
                                      );
                                    }}
                                    onKeyPress={(event: any) => {
                                      handleOnKeyPress(event);
                                    }}
                                  />
                                  <span
                                    className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                    id="basic-addon1"
                                  >
                                    {" "}
                                    {getTitle(dimension?.type)}
                                  </span>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col lg={4} xs={10}>
                            <Row className="align-items-center">
                              <Col md={5}>
                                <label className="fs-16 fw-500 mb-md-0 mb-3">
                                  Discounted price of a{" "}
                                  {getTitle(dimension?.type)}
                                </label>
                              </Col>
                              <Col md={7}>
                                <div
                                  className={`input-group input-group-solid h-60px border ${
                                    validation[productIndex]
                                      ?.sellingDiscountsByQuantities[index]
                                      ?.discountAmt
                                      ? "border-danger"
                                      : ""
                                  }`}
                                >
                                  <span
                                    className="input-group-text fs-16 fw-600 text-dark bg-white border-0 "
                                    id="basic-addon1"
                                  >
                                    TSh
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-600"
                                    placeholder="Type here..."
                                    value={val.discountAmt}
                                    onChange={(event: any) => {
                                      handleQuantityChagne(
                                        event.target.value,
                                        "discountAmt",
                                        productIndex,
                                        index
                                      );
                                    }}
                                    onKeyPress={(event: any) => {
                                      handleOnKeyPress(event);
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col
                            lg={"auto"}
                            xs={"auto"}
                            className="d-flex align-items-lg-end align-self-center"
                          >
                            {dimension.sellingDiscountsByQuantities.length -
                              1 ===
                            index ? (
                              <>
                                <img
                                  src={AddMore}
                                  height={35}
                                  width={35}
                                  alt=""
                                  onClick={(e) => {
                                    handleAddMoreProduct(productIndex);
                                  }}
                                ></img>
                                {dimension?.sellingDiscountsByQuantities
                                  ?.length != 1 && (
                                  <img
                                    className="ms-3"
                                    src={Remove}
                                    height={35}
                                    width={35}
                                    alt=""
                                    onClick={(e) => {
                                      handleRemoveProduct(productIndex, index);
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              <img
                                src={Remove}
                                height={35}
                                width={35}
                                alt=""
                                onClick={(e) => {
                                  handleRemoveProduct(productIndex, index);
                                }}
                              ></img>
                            )}
                          </Col>
                        </Row>
                      </>
                    );
                  }
                )}
              </Col>
            </Row>
          </>
        ) : (
          <></>
        )}
      </>
    );
    return cartoonDetails;
  };
  const renderDozenDetails = (dimension: any, productIndex: number) => {
    let dozenDetails = (
      <>
        <Row className="p-6">
          <h2 className="fs-20 fw-bolder text-primary mb-6">
            Add dozen details
          </h2>
          <Col lg={12}>
            <Row className="align-items-center">
              <Col md="auto">
                <div className="symbol symbol-lg-200px symbol-175px symbol-fixed position-relative">
                  <img
                    className="img-fluid bg-white border"
                    src={BlurCartoon}
                    alt=""
                  />
                  <div className="text-center mt-3">
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={popover}
                    >
                      <Link to="#" className="fs-14 fw-700">
                        How to measure?
                      </Link>
                    </OverlayTrigger>
                  </div>
                </div>
              </Col>
              <Col md={9}>
                <Row className="align-items-center mt-md-0 mt-5">
                  <Col lg={12}>
                    <Row>
                      <Col md={6}>
                        <Row className="align-items-center">
                          <Col lg={4} className="mb-3">
                            <label htmlFor="" className="fs-16 fw-500 required">
                              Original price of a dozen
                            </label>
                          </Col>
                          <Col lg={8} className="mb-3">
                            <div className="input-group input-group-solid h-60px border">
                              <span
                                className={clsx(
                                  "input-group-text fs-16 fw-600 text-dark bg-white "
                                  // validation.dozenPrice
                                  //   ? "border-danger border-right-0"
                                  //   : "border-0 "
                                )}
                                id="basic-addon1"
                              >
                                TSh
                              </span>
                              <input
                                type="text"
                                className={clsx(
                                  "form-control form-control-lg bg-white text-black fs-16 fw-600"
                                  // validation.dozenPrice
                                  //   ? "border-danger border-left-0"
                                  //   : "border-0"
                                )}
                                id="basic-addon1"
                                placeholder="Original price"
                                value={dimension.dozenPrice}
                                onChange={(event: any) => {
                                  // handleChange(
                                  //   event.target.value,
                                  //   combinationIndex,
                                  //   "dozenPrice",
                                  //   productIndex
                                  // );
                                }}
                                onKeyPress={(event: any) => {
                                  // handleOnKeyPress(event, combinationIndex);
                                }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      {dimension.dozenDiscount === "0" ? (
                        <Col md={6}>
                          <Row className="align-items-center">
                            <Col lg={4} className="mb-3">
                              <label htmlFor="" className="fs-16 fw-500">
                                Discounted price of a dozen
                              </label>
                            </Col>
                            <Col lg={8} className="mb-3">
                              <div className="input-group input-group-solid h-60px border">
                                <span
                                  className={clsx(
                                    "input-group-text fs-16 fw-600 text-dark bg-white ",
                                    "border-0 "
                                  )}
                                  id="basic-addon1"
                                >
                                  TSh
                                </span>
                                <input
                                  type="text"
                                  className={clsx(
                                    "form-control form-control-lg bg-white text-black fs-16 fw-600",
                                    "border-0"
                                  )}
                                  placeholder="Discount price"
                                  value={dimension.dozenDiscountPrice}
                                  onChange={(event: any) => {
                                    // handleChange(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "dozenDiscountPrice",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                              </div>
                            </Col>
                            {false ? (
                              <>
                                <Col lg={4} className="mb-3"></Col>
                                <Col lg={8} className="mb-3">
                                  <label
                                    htmlFor=""
                                    className="fs-12 fw-500 text-danger"
                                  >
                                    Discount Price must be less than or equal to
                                    original price
                                  </label>
                                </Col>
                              </>
                            ) : (
                              <></>
                            )}
                          </Row>
                        </Col>
                      ) : (
                        <></>
                      )}
                    </Row>
                  </Col>
                  <Col md={12}>
                    <label htmlFor="" className="fs-16 fw-500 mb-3 mt-2">
                      Do you offer discount by total number of quantities?
                    </label>
                    <Row className="align-items-center">
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="0"
                            id="flexRadioChecked"
                            checked={dimension.dozenDiscount === "0"}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "dozenDiscount",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            No
                          </label>
                        </div>
                      </Col>
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="1"
                            id="flexRadioChecked"
                            checked={dimension.dozenDiscount === "1"}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "dozenDiscount",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            Yes
                          </label>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  {/* <Col lg={12} className="mt-3">
                    <Row className="align-items-center mb-5">
                      <Col md={12} lg={3}>
                        <label className="fs-16 fw-500 mt-4 mb-4">
                          Dozen dimensions (cm)
                        </label>
                      </Col>
                      <Col sm={4} lg={3}>
                        <div className="input-group input-group-solid h-60px mb-sm-0 mb-5">
                          <input
                            type="text"
                            className={clsx(
                              "form-control form-control-lg border border-r8px bg-white text-black fs-16 fw-500",
                              validation.dozenDimenisionL ? "border-danger" : ""
                            )}
                            placeholder="Length"
                            value={dimension.dozenDimenisionL}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "dozenDimenisionL",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <span
                            className="input-group-text fs-16 fw-500 text-dark bg-light border-0 "
                            id="basic-addon1"
                          >
                            L
                          </span>
                        </div>
                      </Col>
                      <Col sm={4} lg={3}>
                        <div className="input-group input-group-solid h-60px mb-sm-0 mb-5">
                          <input
                            type="text"
                            className={clsx(
                              "form-control form-control-lg border border-r8px bg-white text-black fs-16 fw-500",
                              validation.dozenDimenisionW ? "border-danger" : ""
                            )}
                            placeholder="Width"
                            value={dimension.dozenDimenisionW}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "dozenDimenisionW",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <span
                            className="input-group-text fs-16 fw-500 text-dark bg-light border-0 "
                            id="basic-addon1"
                          >
                            W
                          </span>
                        </div>
                      </Col>
                      <Col sm={4} lg={3}>
                        <div className="input-group input-group-solid h-60px mb-sm-0 mb-5">
                          <input
                            type="text"
                            className={clsx(
                              "form-control form-control-lg border border-r8px bg-white text-black fs-16 fw-500",
                              validation.dozenDimenisionH ? "border-danger" : ""
                            )}
                            placeholder="Height"
                            value={dimension.dozenDimenisionH}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "dozenDimenisionH",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <span
                            className="input-group-text fs-16 fw-500 text-dark bg-light border-0 "
                            id="basic-addon1"
                          >
                            H
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      <Col lg={3} md={12}>
                        <label className="fs-16 fw-500 mt-sm-4 mt-0 mb-4">
                          Dozen weight (kg)
                        </label>
                      </Col>
                      <Col lg={2} md={6}>
                        <input
                          type="text"
                          placeholder="Weight"
                          className={clsx(
                            "form-control form-control-lg border border-r8px bg-white text-black h-60px fs-16 fw-500",
                            validation.dozenWeight ? "border-danger" : ""
                          )}
                          value={dimension.dozenWeight}
                          onChange={(event: any) => {
                            // handleChange(
                            //   event.target.value,
                            //   combinationIndex,
                            //   "dozenWeight",
                            //   productIndex
                            // );
                          }}
                          onKeyPress={(event: any) => {
                            // handleOnKeyPress(event, combinationIndex);
                          }}
                        />
                      </Col>
                    </Row>
                  </Col> */}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        {dimension.dozenDiscount === "1" ? (
          <>
            <div className="separator my-2"></div>
            <Row className="p-6 pb-8">
              <Col lg={12}>
                <div>
                  <h2 className="fs-22 fw-bolder text-dark">
                    Discount by total number of quantities
                  </h2>
                </div>
                {dimension.dozenQuantities.map((val: any, index: number) => {
                  return (
                    <>
                      <Row className="align-items-center mt-6">
                        <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                          <Row className="align-items-center">
                            <Col md={5}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Minimum qty.
                              </label>
                            </Col>
                            <Col md={7}>
                              <div className="input-group input-group-solid h-60px border">
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                  placeholder="Original price"
                                  value={val.min}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "dozenQuantities",
                                    //   index,
                                    //   "min",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                                <span
                                  className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  dozen
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                          <Row className="align-items-center">
                            <Col md={5}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Maximum qty.
                              </label>
                            </Col>
                            <Col md={7}>
                              <div className="input-group input-group-solid h-60px border">
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                  placeholder="Original price"
                                  value={val.max}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "dozenQuantities",
                                    //   index,
                                    //   "max",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                                <span
                                  className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  dozen
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={5} xs={10}>
                          <Row className="align-items-center">
                            <Col md={4}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Discounted price of a dozen
                              </label>
                            </Col>
                            <Col md={8}>
                              <div className="input-group input-group-solid h-60px border">
                                <span
                                  className="input-group-text fs-16 fw-600 text-dark bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  TSh
                                </span>
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-600"
                                  placeholder="Type here..."
                                  value={val.discount}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "dozenQuantities",
                                    //   index,
                                    //   "discount",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col
                          lg={"auto"}
                          xs={"auto"}
                          className="d-flex align-items-lg-end align-self-center"
                        >
                          {dimension.dozenQuantities.length - 1 === index ? (
                            <img
                              src={AddMore}
                              height={35}
                              width={35}
                              alt=""
                              onClick={(e) => {
                                // handleAddMoreProduct(
                                //   combinationIndex,
                                //   "dozenQuantities",
                                //   productIndex
                                // );
                              }}
                            ></img>
                          ) : (
                            <img
                              src={Remove}
                              height={35}
                              width={35}
                              alt=""
                              onClick={(e) => {
                                // handleRemoveProduct(
                                //   combinationIndex,
                                //   index,
                                //   "dozenQuantities",
                                //   productIndex
                                // );
                              }}
                            ></img>
                          )}
                        </Col>
                      </Row>
                    </>
                  );
                })}
              </Col>
            </Row>
          </>
        ) : (
          <></>
        )}
      </>
    );
    return dozenDetails;
  };
  const renderPiecesDetails = (dimension: any, productIndex: number) => {
    let piecesDetails = (
      <>
        <Row className="p-6">
          <h2 className="fs-20 fw-bolder text-primary mb-6">
            Add piece details
          </h2>
          <Col lg={12}>
            <Row className="align-items-center">
              <Col md={3}>
                <div className="symbol symbol-lg-200px symbol-175px symbol-fixed position-relative">
                  <img
                    className="img-fluid bg-white border"
                    src={BlurCartoon}
                    alt=""
                    height={200}
                    width={200}
                  />
                  <div className="text-center mt-3">
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={popover}
                    >
                      <Link to="#" className="fs-14 fw-700">
                        How to measure?
                      </Link>
                    </OverlayTrigger>
                  </div>
                </div>
              </Col>
              <Col md={9}>
                <Row className="align-items-center mt-md-0 mt-5">
                  <Col lg={12}>
                    <Row>
                      <Col md={6}>
                        <Row className="align-items-center">
                          <Col lg={4} className="mb-3">
                            <label htmlFor="" className="fs-16 fw-500 required">
                              Original price of a piece
                            </label>
                          </Col>
                          <Col lg={8} className="mb-3">
                            <div className="input-group input-group-solid h-60px border">
                              <span
                                // className="input-group-text fs-16 fw-600 text-dark bg-white border-0 "
                                className={clsx(
                                  "input-group-text fs-16 fw-600 text-dark bg-white "
                                  // validation.piecePrice
                                  //   ? "border-danger border-right-0"
                                  //   : "border-0 "
                                )}
                                id="basic-addon1"
                              >
                                TSh
                              </span>
                              <input
                                type="text"
                                className={clsx(
                                  "form-control form-control-lg bg-white text-black fs-16 fw-600 "
                                  // validation.piecePrice
                                  //   ? "border-danger border-left-0"
                                  //   : "border-0 "
                                )}
                                placeholder="Original price"
                                value={dimension.piecePrice}
                                onChange={(event: any) => {
                                  // handleChange(
                                  //   event.target.value,
                                  //   combinationIndex,
                                  //   "piecePrice",
                                  //   productIndex
                                  // );
                                }}
                                onKeyPress={(event: any) => {
                                  // handleOnKeyPress(event, combinationIndex);
                                }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      {dimension.pieceDiscount === "0" ? (
                        <Col md={6}>
                          <Row className="align-items-center">
                            <Col lg={4} className="mb-3">
                              <label htmlFor="" className="fs-16 fw-500">
                                Discounted price of a piece
                              </label>
                            </Col>
                            <Col lg={8} className="mb-3">
                              <div className="input-group input-group-solid h-60px border">
                                <span
                                  className={clsx(
                                    "input-group-text fs-16 fw-600 text-dark bg-white"
                                    // validation.pieceDiscountPrice
                                    //   ? "border-danger border-right-0"
                                    //   : "border-0 "
                                  )}
                                  id="basic-addon1"
                                >
                                  TSh
                                </span>
                                <input
                                  type="text"
                                  className={clsx(
                                    "form-control form-control-lg bg-white text-black fs-16 fw-600 "
                                    // validation.pieceDiscountPrice
                                    //   ? "border-danger border-left-0"
                                    //   : "border-0 "
                                  )}
                                  placeholder="Original price"
                                  value={dimension.pieceDiscountPrice}
                                  onChange={(event: any) => {
                                    // handleChange(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "pieceDiscountPrice",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                              </div>
                            </Col>
                            {false ? (
                              <>
                                <Col lg={4} className="mb-3"></Col>
                                <Col lg={8} className="mb-3">
                                  <label
                                    htmlFor=""
                                    className="fs-12 fw-500 text-danger"
                                  >
                                    Discount Price must be less than or equal to
                                    original price
                                  </label>
                                </Col>
                              </>
                            ) : (
                              <></>
                            )}
                          </Row>
                        </Col>
                      ) : (
                        <></>
                      )}
                    </Row>
                  </Col>
                  <Col md={12}>
                    <label htmlFor="" className="fs-16 fw-500 mb-3 mt-2">
                      Do you offer discount by total number of quantities?
                    </label>
                    <Row className="align-items-center">
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="0"
                            id="flexRadioChecked"
                            checked={dimension.pieceDiscount === "0"}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "pieceDiscount",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            No
                          </label>
                        </div>
                      </Col>
                      <Col xs="auto">
                        <div className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="1"
                            id="flexRadioChecked"
                            checked={dimension.pieceDiscount === "1"}
                            onChange={(event: any) => {
                              // handleChange(
                              //   event.target.value,
                              //   combinationIndex,
                              //   "pieceDiscount",
                              //   productIndex
                              // );
                            }}
                            onKeyPress={(event: any) => {
                              // handleOnKeyPress(event, combinationIndex);
                            }}
                          />
                          <label className="form-check-label fs-16 fw-600 text-dark">
                            Yes
                          </label>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        {dimension.pieceDiscount === "1" ? (
          <>
            <div className="separator my-2"></div>
            <Row className="p-6 pb-8">
              <Col lg={12}>
                <div>
                  <h2 className="fs-22 fw-bolder text-dark">
                    Discount by total number of quantities
                  </h2>
                </div>
                {dimension.pieceQuantities.map((val: any, index: number) => {
                  return (
                    <>
                      <Row className="align-items-center mt-6">
                        <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                          <Row className="align-items-center">
                            <Col md={5}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Minimum qty.
                              </label>
                            </Col>
                            <Col md={7}>
                              <div className="input-group input-group-solid h-60px border">
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                  placeholder="Original price"
                                  value={val.min}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "pieceQuantities",
                                    //   index,
                                    //   "min",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                                <span
                                  className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  piece
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={3} sm={6} className="mb-lg-0 mb-5">
                          <Row className="align-items-center">
                            <Col md={5}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Maximum qty.
                              </label>
                            </Col>
                            <Col md={7}>
                              <div className="input-group input-group-solid h-60px border">
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-500"
                                  placeholder="Original price"
                                  value={val.max}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "pieceQuantities",
                                    //   index,
                                    //   "max",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                                <span
                                  className="input-group-text fs-16 fw-500 text-gray bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  piece
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={5} xs={10}>
                          <Row className="align-items-center">
                            <Col md={4}>
                              <label className="fs-16 fw-500 mb-md-0 mb-3">
                                Discounted price of a piece
                              </label>
                            </Col>
                            <Col md={8}>
                              <div className="input-group input-group-solid h-60px border">
                                <span
                                  className="input-group-text fs-16 fw-600 text-dark bg-white border-0 "
                                  id="basic-addon1"
                                >
                                  TSh
                                </span>
                                <input
                                  type="text"
                                  className="form-control form-control-lg border-0 bg-white text-black fs-16 fw-600"
                                  placeholder="Type here..."
                                  value={val.discount}
                                  onChange={(event: any) => {
                                    // handleQuantityChangeProduct(
                                    //   event.target.value,
                                    //   combinationIndex,
                                    //   "pieceQuantities",
                                    //   index,
                                    //   "discount",
                                    //   productIndex
                                    // );
                                  }}
                                  onKeyPress={(event: any) => {
                                    // handleOnKeyPress(event, combinationIndex);
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col
                          lg={"auto"}
                          xs={"auto"}
                          className="d-flex align-items-lg-end align-self-center"
                        >
                          {dimension.pieceQuantities.length - 1 === index ? (
                            <img
                              src={AddMore}
                              height={35}
                              width={35}
                              alt=""
                              // onClick={(e) => {
                              //   handleAddMoreProduct(
                              //     combinationIndex,
                              //     "pieceQuantities",
                              //     productIndex
                              //   );
                              // }}
                            ></img>
                          ) : (
                            <img
                              src={Remove}
                              height={35}
                              width={35}
                              alt=""
                              // onClick={(e) => {
                              //   handleRemoveProduct(
                              //     combinationIndex,
                              //     index,
                              //     "pieceQuantities",
                              //     productIndex
                              //   );
                              // }}
                            ></img>
                          )}
                        </Col>
                      </Row>
                    </>
                  );
                })}
              </Col>
            </Row>
          </>
        ) : (
          <></>
        )}
      </>
    );
    return piecesDetails;
  };

  const getLogsMessage = () => {
    let message: string[] = [];
    variantData?.quantityTypes?.forEach((item: any, index: number) => {
      let oldObj = oldData?.quantityTypes[index];
      if (oldObj.sellingAmount != item?.sellingAmount) {
        message.push(
          `Selling Amount updated from ${oldObj?.sellingAmount || 0} to ${
            item?.sellingAmount || 0
          }`
        );
      }
      if (
        oldObj.sellingDiscountByQuantitiesEnabled !=
        item.sellingDiscountByQuantitiesEnabled
      ) {
        message.push(
          `Discount by quantity has been ${
            item.sellingDiscountByQuantitiesEnabled ? "enabled" : "disabled"
          }`
        );
        if (item.sellingDiscountByQuantitiesEnabled) {
          let temp: string[] = [];
          item?.sellingDiscountsByQuantities.forEach((qItem: any) => {
            temp.push(
              `Min Quantity ${qItem.min} Max Quantity ${
                qItem.max
              } Discount Amount ${qItem?.discountAmt || 0}`
            );
          });
          if (temp.length) {
            temp.unshift("Selling discount by quantities updated");
            message.push(temp.join(", "));
          }
        } else {
          if (oldObj.sellingDiscountAmt && item?.sellingDiscountAmt) {
            message.push(
              `Selling Discount Amount updated from ${
                oldObj?.sellingDiscountAmt || 0
              } to ${item?.sellingDiscountAmt || 0}`
            );
          }
        }
      } else {
        if (item.sellingDiscountByQuantitiesEnabled) {
          let temp: string[] = [];
          item?.sellingDiscountsByQuantities?.forEach(
            (qItem: any, qIndex: number) => {
              let tempObj = oldObj?.sellingDiscountsByQuantities[qIndex];
              if (tempObj) {
                if (tempObj?.min != qItem?.min) {
                  temp.push(
                    `Min Quantity Updated from  ${tempObj?.min} to ${qItem.min} `
                  );
                }
                if (tempObj?.max != qItem?.max) {
                  temp.push(
                    `Max Quantity Updated from  ${tempObj?.max} to ${qItem.max} `
                  );
                }
                if (tempObj?.discountAmt != qItem?.discountAmt) {
                  temp.push(
                    `Discount Amount Updated from  ${tempObj?.discountAmt} to ${qItem.discountAmt} `
                  );
                }
              } else {
                temp.push(
                  `Min Quantity ${qItem.min} Max Quantity ${
                    qItem.max
                  } Discount Amount ${qItem?.discountAmt || 0}`
                );
              }
            }
          );
          if (temp.length) {
            temp.unshift("Selling discount by quantities updated");
            message.push(temp.join(", "));
          }
        } else {
          if (oldObj?.sellingDiscountAmt != item?.sellingDiscountAmt) {
            message.push(
              `Selling Discount Amount updated from ${
                oldObj?.sellingDiscountAmt || 0
              } to ${item?.sellingDiscountAmt || 0}`
            );
          }
        }
      }
    });
    if (message.length) {
      message.unshift(
        `${variantData?.variant?.title} ${
          getTitle(variantData?.quantityTypes[0]?.type) || ""
        }`
      );
    }
    return message;
  };

  return (
    <>
      <Modal
        show={props.show}
        onHide={props.onHide}
        dialogClassName="modal-dialog-centered min-w-lg-590px"
        className="border-r10px"
        contentClassName="p-5"
        size="xl"
      >
        <Modal.Header className="border-bottom-0 pb-6 text-center mx-auto">
          <div className="symbol symbol-md-40px symbol-35px close-inner-top-3 mb-4">
            <img
              width={40}
              height={40}
              src={CrossSvg}
              alt="closebutton"
              onClick={props.onHide}
            />
          </div>
          <Modal.Title className="fs-26 fw-bolder mw-lg-350px">
            {props.variantData?.variant?.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-0 text-center">
          <Card>
            {variantData ? (
              variantData?.quantityTypes?.map((item: any, itemIndex: any) => {
                return (
                  <>
                    {/* {item?.type == CartonWithDozens ? ( */}
                    <Card.Body
                      className={clsx(
                        "border border-top-0 bg-light p-0"
                        // generalDataValidation[index].combinations[itemIndex]
                        //   ?.validation
                        //   ? ""
                        //   : ""
                      )}
                    >
                      {renderCartoonDetails(item, itemIndex)}
                    </Card.Body>
                    {/* ) : (
                      <></>
                    )} */}
                  </>
                );
              })
            ) : (
              <></>
            )}
          </Card>
        </Modal.Body>
        <Modal.Footer className="justify-content-center pt-1 border-top-0">
          <Button className="uw-btn uw-btn-primary uw-btn-lg"
            variant="primary"
            size="lg"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <span className="indicator-progress" style={{ display: "block" }}>
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateSellingPrice;
